import type { ReactNode } from 'react'

import Image from 'next/image'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { siteConfig } from '@/constants/site'

export const AboutIntro = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, reduced }) => {
        if (reduced) return

        const intro = gsap.timeline({ defaults: { ease: 'nuvea' } })

        intro
            .from(select('[data-about-title] .reveal-mask > span'), {
                yPercent: 115,
                duration: 1.15,
                stagger: 0.06
            })
            .from(
                select('[data-about-copy] > *'),
                { y: 24, opacity: 0, duration: 0.9, stagger: 0.09 },
                '-=0.7'
            )

        // The brand frame opens outward from a letterbox slit.
        const frame = select('[data-about-frame]')[0]

        if (!frame) return

        gsap.fromTo(
            frame,
            { clipPath: 'inset(22% 0% 22% 0% round 1rem)' },
            {
                clipPath: 'inset(0% 0% 0% 0% round 1rem)',
                duration: 1.6,
                ease: 'nuvea',
                scrollTrigger: { trigger: frame, start: 'top 82%' }
            }
        )

        gsap.fromTo(
            frame.querySelector('img'),
            { scale: 1.25, yPercent: -5 },
            {
                scale: 1.08,
                yPercent: 5,
                ease: 'none',
                scrollTrigger: {
                    trigger: frame,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 0.8
                }
            }
        )

        gsap.from(select('[data-about-frame-label]'), {
            y: 22,
            opacity: 0,
            duration: 1,
            stagger: 0.1,
            ease: 'nuvea',
            scrollTrigger: { trigger: frame, start: 'top 60%' }
        })
    })

    return (
        <section ref={scope} id='beginning' className='pt-10 md:pt-16'>
            <div className='shell-x mx-auto w-full max-w-[1760px]'>
                <SplitWords
                    data-about-title=''
                    as='h1'
                    lines={['Where it started']}
                    className='type-display text-ink'
                />

                <div className='mt-10 grid grid-cols-1 gap-8 md:mt-14 md:grid-cols-[10rem_1fr] md:gap-12 lg:grid-cols-[14rem_minmax(0,60ch)_1fr]'>
                    <p className='type-label h-fit w-fit rounded-full bg-sage px-3 py-1.5 text-ink'>
                        Where it started
                    </p>

                    <div data-about-copy='' className='flex flex-col gap-6'>
                        <p className='type-lead text-ink'>
                            Camille spent eleven years in a Lyon clinic mostly undoing damage that expensive routines
                            had done. Ingrid spent nine measuring how quickly a compromised
                            barrier loses water.{' '}
                            <strong className='font-semibold'>
                                They started Nuvéa because the honest prescription was almost always to use less, and
                                nothing on the market was built for that.
                            </strong>
                        </p>

                        <p className='type-copy text-slate-soft'>
                            One jar. Five actives at doses the research actually used. Made in Switzerland, refilled
                            rather than replaced, and sold at a price that makes finishing
                            it the obvious thing to do.
                        </p>
                    </div>
                </div>
            </div>

            <div className='shell-x mx-auto mt-14 w-full max-w-[1760px] md:mt-20'>
                <div
                    data-about-frame=''
                    className='relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-shell md:aspect-[21/9]'
                >
                    <Image
                        src='/images/about-portrait.png'
                        alt='Two women photographed close together, skin clear and unretouched'
                        fill
                        priority
                        sizes='100vw'
                        className='object-cover will-change-transform'
                    />

                    <span
                        aria-hidden='true'
                        className='absolute inset-0 bg-gradient-to-t from-ink/45 via-transparent to-transparent'
                    />

                    <p
                        data-about-frame-label=''
                        aria-hidden='true'
                        className='type-h1 absolute inset-0 flex items-center justify-center text-cream/45 select-none'
                    >
                        {siteConfig.wordmark}

                        <span className='self-start pt-[0.6em] text-[0.3em] leading-none'>
                            &trade;
                        </span>
                    </p>

                    <div className='absolute inset-x-0 bottom-0 flex items-end justify-between p-6 md:p-10'>
                        <div data-about-frame-label=''>
                            <p className='type-micro text-cream/70'>
                                Est
                            </p>

                            <p className='type-h3 text-cream'>
                                {siteConfig.establishedIn}
                            </p>
                        </div>

                        <div data-about-frame-label='' className='text-right'>
                            <p className='type-micro text-cream/70'>
                                Made in
                            </p>

                            <p className='type-h3 text-cream'>
                                {siteConfig.madeIn}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
